/**
 * Roulette WebRTC helper (V6) — TURN-RELAY-ONLY, per realtime-protocol §5:
 * ICE config comes from GET /calls/config?context=roulette (force_relay:
 * true, TURN servers only — strangers never exchange IPs); the client pins
 * iceTransportPolicy 'relay' AND the server independently rejects non-relay
 * candidates on the signaling relay (RELAY_REQUIRED) — defense on both ends.
 *
 * /calls/config is V3b-owned; when absent (404) or when the server flag
 * roulette_av_enabled is off, AV simply stays unavailable — the text floor
 * always works (D-009 honest gate).
 */
import { wsCommand } from "../../lib/ws.js";
import { rouletteApi } from "./raw-api.js";

interface IceConfigResponse {
  ice_servers: Array<{ urls: string | string[]; username?: string; credential?: string }>;
  force_relay?: boolean;
}

export interface RouletteAvSession {
  pc: RTCPeerConnection;
  localStream: MediaStream;
  close: () => void;
}

/** Fetch roulette ICE config; null ⇒ AV unavailable (route absent / no TURN). */
export async function fetchRouletteIce(): Promise<RTCIceServer[] | null> {
  try {
    const cfg = await rouletteApi<IceConfigResponse>("GET", "/calls/config?context=roulette");
    if (!cfg?.ice_servers?.length) return null;
    return cfg.ice_servers as RTCIceServer[];
  } catch {
    return null; // V3b route not deployed / TURN not verified — text-only
  }
}

/**
 * Open the local media + peer connection for a roulette AV session. The
 * caller wires remote tracks + signaling subscriptions; `initiator` (from
 * the roulette.paired payload) creates the offer.
 */
export async function startRouletteAv(opts: {
  sessionId: string;
  initiator: boolean;
  iceServers: RTCIceServer[];
  camera: boolean;
  mic: boolean;
  onRemoteStream: (stream: MediaStream) => void;
}): Promise<RouletteAvSession> {
  const localStream = await navigator.mediaDevices.getUserMedia({
    video: opts.camera,
    audio: opts.mic,
  });

  const pc = new RTCPeerConnection({
    iceServers: opts.iceServers,
    iceTransportPolicy: "relay", // NORMATIVE — never host/srflx in the pit
  });

  for (const track of localStream.getTracks()) pc.addTrack(track, localStream);

  pc.ontrack = (ev) => {
    if (ev.streams[0]) opts.onRemoteStream(ev.streams[0]);
  };
  pc.onicecandidate = (ev) => {
    if (!ev.candidate) return;
    // Belt: never even SEND a non-relay candidate (the server drops them too).
    if (ev.candidate.candidate && !/typ\s+relay/.test(ev.candidate.candidate)) return;
    void wsCommand("roulette.ice", {
      session_id: opts.sessionId,
      candidate: ev.candidate.toJSON(),
    }).catch(() => {});
  };

  if (opts.initiator) {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await wsCommand("roulette.sdp", {
      session_id: opts.sessionId,
      desc: { type: offer.type, sdp: offer.sdp },
    }).catch(() => {});
  }

  const close = () => {
    for (const track of localStream.getTracks()) track.stop();
    pc.close();
  };
  return { pc, localStream, close };
}

/** Handle an incoming roulette.sdp frame (offer → answer, answer → set). */
export async function handleRemoteSdp(
  av: RouletteAvSession,
  sessionId: string,
  desc: { type: string; sdp: string },
): Promise<void> {
  await av.pc.setRemoteDescription({ type: desc.type as RTCSdpType, sdp: desc.sdp });
  if (desc.type === "offer") {
    const answer = await av.pc.createAnswer();
    await av.pc.setLocalDescription(answer);
    await wsCommand("roulette.sdp", {
      session_id: sessionId,
      desc: { type: answer.type, sdp: answer.sdp },
    }).catch(() => {});
  }
}

export async function handleRemoteIce(av: RouletteAvSession, candidate: unknown): Promise<void> {
  try {
    await av.pc.addIceCandidate(candidate as RTCIceCandidateInit);
  } catch {
    /* stale candidate after teardown — ignore */
  }
}
